/**
 * Run Q / Q-5 — Settings sections, and the URL each one lives at.
 *
 * Settings used to keep its section in component state only, so every section
 * was the same URL and nothing could link to one. Integrations, the Audit Log
 * and Data & Privacy are sections now rather than sidebar destinations, and
 * their old top-level URLs redirect here — which only works if a section IS a
 * URL. Kept as data so the redirect map and the nav are pinned together.
 */
export type SettingsSection =
  | 'identity' | 'team' | 'modalities' | 'tools' | 'outcomes' | 'pathway'
  | 'reports' | 'checker' | 'integrations' | 'security' | 'type' | 'billing'
  | 'audit' | 'privacy';

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  'identity', 'team', 'modalities', 'tools', 'outcomes', 'pathway',
  'reports', 'checker', 'integrations', 'security', 'type', 'billing', 'audit', 'privacy',
];

/** Sections only a clinic admin may open. */
export const ADMIN_ONLY_SECTIONS: readonly SettingsSection[] = ['billing', 'audit', 'privacy'];

export const DEFAULT_SECTION: SettingsSection = 'identity';

/**
 * `/settings/clinic` is the sidebar's link and every existing bookmark, so it
 * keeps working and means "the first section".
 */
export const LEGACY_SETTINGS_PATHS: Record<string, SettingsSection> = {
  clinic: DEFAULT_SECTION,
  '': DEFAULT_SECTION,
};

/** Which section a /settings/... path selects. Unknown paths fall back. */
export function sectionFromPath(pathname: string): SettingsSection {
  const seg = pathname.replace(/^\/settings\/?/, '').split('/')[0] ?? '';
  if (SETTINGS_SECTIONS.includes(seg as SettingsSection)) return seg as SettingsSection;
  return LEGACY_SETTINGS_PATHS[seg] ?? DEFAULT_SECTION;
}

export const sectionPath = (s: SettingsSection): string => `/settings/${s}`;

/**
 * Q-5 — the three destinations that left the sidebar. Old links, bookmarks and
 * anything a clinic pasted into a chat keep working.
 */
export const MOVED_ROUTES: Record<string, string> = {
  '/integrations': '/settings/integrations',
  '/admin/audit': '/settings/audit',
  '/admin/gdpr': '/settings/privacy',
};
